import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Wallet,
  Check,
  X,
  CalendarDays,
  Filter,
  IndianRupee,
} from "lucide-react";

import api from "../constant/api";
import { toast } from "react-toastify";

const CATEGORIES = [
  "travel",
  "food",
  "accommodation",
  "fuel",
  "misc",
];

const FILTERS = [
  { value: "all", label: "All" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const getToday = () => new Date().toISOString().slice(0, 10);

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getCurrentYear = () => String(new Date().getFullYear());

export default function GarmentsExpenses() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user_id = user.user_id;
  const role = user.role;
  const selectedBranch = localStorage.getItem("selectedBranch") || "";

  const isAdmin = role === "admin" || role === "superadmin";

  const [expenses, setExpenses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState("monthly");
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());

  // Form State
  const [form, setForm] = useState({
    category: "travel",
    amount: "",
    expense_date: getToday(),
    description: "",
  });

  const load = async () => {
    if (!user_id) {
      toast.error("User ID not found");
      return;
    }

    try {
      setLoadingExpenses(true);
      const res = await api.get(
        `/GarmentsProduct/getExpenses?user_id=${encodeURIComponent(user_id)}&role=${role}&branch=${selectedBranch}`
      );

      if (res.data?.success) {
        setExpenses(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setExpenses([]);
        toast.error(res.data?.msg || "Failed to load expenses");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to load expenses");
    } finally {
      setLoadingExpenses(false);
    }
  };

  useEffect(() => {
    if (user_id) {
      load();
    }
  }, [user_id, role]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      category: "travel",
      amount: "",
      expense_date: getToday(),
      description: "",
    });
  };

  const handleAdd = async () => {
    const amount = Number(form.amount);

    if (!form.amount || !Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!form.expense_date) {
      toast.error("Select expense date");
      return;
    }

    try {
      setBusy(true);
      const payload = {
        user_id: Number(user_id),
        branch: selectedBranch,
        category: form.category || "misc",
        amount: amount,
        expense_date: form.expense_date,
        description: form.description?.trim() || "",
      };

      const res = await api.post("/GarmentsProduct/addExpense", payload);

      if (res.data?.success) {
        toast.success("Expense submitted successfully ✅");
        setShowAdd(false);
        resetForm();
        await load();
      } else {
        toast.error(res.data?.msg || "Failed to submit expense");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to submit expense");
    } finally {
      setBusy(false);
    }
  };

  const handleDecision = async (expense_id, status) => {
    try {
      const res = await api.put(`/GarmentsProduct/decideExpense/${expense_id}`, { status });
      if (res.data?.success) {
        toast.success(status === "approved" ? "Expense approved ✅" : "Expense rejected ❌");
        await load();
      } else {
        toast.error(res.data?.msg || "Failed to update expense");
      }
    } catch (error) {
      toast.error("Failed to update expense");
    }
  };

  const statusColor = (status) => {
    if (status === "approved") return "bg-green-100 text-green-700";
    if (status === "rejected") return "bg-red-100 text-red-700";
    return "bg-amber-100 text-amber-700";
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-IN");
  };

  const getDateString = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const filteredExpenses = expenses.filter((expense) => {
    const expenseDate = getDateString(expense.expense_date);
    if (!expenseDate) return false;

    if (filterType === "all") return true;
    if (filterType === "daily") return expenseDate === selectedDate;
    if (filterType === "monthly") return expenseDate.startsWith(selectedMonth);
    if (filterType === "yearly") return expenseDate.startsWith(selectedYear);
    return true;
  });

  const totalAmount = filteredExpenses.reduce((sum, ex) => sum + Number(ex.amount || 0), 0);
  const approvedAmount = filteredExpenses.filter(ex => ex.status === "approved").reduce((sum, ex) => sum + Number(ex.amount || 0), 0);
  const pendingAmount = filteredExpenses.filter(ex => ex.status === "pending").reduce((sum, ex) => sum + Number(ex.amount || 0), 0);
  const rejectedAmount = filteredExpenses.filter(ex => ex.status === "rejected").reduce((sum, ex) => sum + Number(ex.amount || 0), 0);

  return (
    <div className="w-full p-4 space-y-4 bg-slate-50 min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-4 rounded-2xl shadow-sm border">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Garments Expenses</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {isAdmin ? "Review and approve branch expenses" : "Submit your daily operational expenses"}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow transition"
        >
          <Plus size={16} /> Add Expense
        </button>
      </div>

      {/* FILTER SECTION */}
      <div className="bg-white border rounded-2xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-indigo-600" />
          <h3 className="font-bold text-xs text-gray-800 uppercase">Expense Filter</h3>
        </div>

        <div className="grid grid-cols-2 md:flex gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setFilterType(filter.value)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
                filterType === filter.value
                  ? "bg-indigo-600 text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {filterType === "daily" && (
          <div className="max-w-xs space-y-1">
            <label className="text-[11px] font-semibold text-gray-500">Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border w-full px-3 py-2 rounded-xl text-xs font-semibold outline-none focus:border-indigo-500"
            />
          </div>
        )}

        {filterType === "monthly" && (
          <div className="max-w-xs space-y-1">
            <label className="text-[11px] font-semibold text-gray-500">Select Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border w-full px-3 py-2 rounded-xl text-xs font-semibold outline-none focus:border-indigo-500"
            />
          </div>
        )}
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-1">
          <p className="text-[11px] font-bold text-gray-500 uppercase">Total Expense</p>
          <p className="text-lg md:text-xl font-extrabold text-indigo-600">₹{totalAmount.toLocaleString("en-IN")}</p>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-1">
          <p className="text-[11px] font-bold text-gray-500 uppercase">Approved</p>
          <p className="text-lg md:text-xl font-extrabold text-green-600">₹{approvedAmount.toLocaleString("en-IN")}</p>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-1">
          <p className="text-[11px] font-bold text-gray-500 uppercase">Pending</p>
          <p className="text-lg md:text-xl font-extrabold text-amber-600">₹{pendingAmount.toLocaleString("en-IN")}</p>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-1">
          <p className="text-[11px] font-bold text-gray-500 uppercase">Rejected</p>
          <p className="text-lg md:text-xl font-extrabold text-red-600">₹{rejectedAmount.toLocaleString("en-IN")}</p>
        </div>
      </div>

      {/* EXPENSES TABLE */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-3.5 border-b flex justify-between items-center bg-slate-50/50">
          <h3 className="text-xs font-bold uppercase text-gray-700">Expense Records ({filteredExpenses.length})</h3>
          {loadingExpenses && <span className="text-xs text-gray-400">Loading...</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-600 border-b">
              <tr>
                {isAdmin && <th className="p-3 font-semibold">User</th>}
                <th className="p-3 font-semibold">Category</th>
                <th className="p-3 font-semibold">Description</th>
                <th className="p-3 font-semibold">Date</th>
                <th className="p-3 font-semibold text-right">Amount</th>
                <th className="p-3 font-semibold text-center">Status</th>
                {isAdmin && <th className="p-3 font-semibold text-center">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 5} className="text-center p-8 text-gray-400">
                    No expenses found.
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense) => (
                  <tr key={expense.expense_id} className="hover:bg-slate-50 transition">
                    {isAdmin && (
                      <td className="p-3 font-bold text-slate-800">
                        {expense.salesman_name || "Unknown"}
                      </td>
                    )}
                    <td className="p-3 capitalize font-medium">{expense.category}</td>
                    <td className="p-3 text-gray-600">{expense.description || "—"}</td>
                    <td className="p-3">{formatDate(expense.expense_date)}</td>
                    <td className="p-3 text-right font-extrabold text-slate-900">
                      ₹{Number(expense.amount || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${statusColor(expense.status)}`}>
                        {expense.status || "pending"}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="p-3 text-center">
                        {expense.status === "pending" ? (
                          <div className="flex justify-center gap-1.5">
                            <button
                              onClick={() => handleDecision(expense.expense_id, "approved")}
                              className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded-lg transition"
                              title="Approve"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => handleDecision(expense.expense_id, "rejected")}
                              className="bg-rose-600 hover:bg-rose-700 text-white p-1.5 rounded-lg transition"
                              title="Reject"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD EXPENSE MODAL */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-3xl p-5 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-sm font-bold text-slate-900">Add New Expense</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-gray-600">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                  className="w-full border p-2.5 rounded-xl font-semibold mt-1 outline-none"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat} className="capitalize">
                      {cat.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-600">Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Enter amount"
                  value={form.amount}
                  onChange={(e) => handleFormChange("amount", e.target.value)}
                  className="w-full border p-2.5 rounded-xl font-bold mt-1 outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-gray-600">Expense Date</label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => handleFormChange("expense_date", e.target.value)}
                  className="w-full border p-2.5 rounded-xl font-semibold mt-1 outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-gray-600">Description</label>
                <textarea
                  placeholder="Add note or details..."
                  rows="3"
                  value={form.description}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  className="w-full border p-2.5 rounded-xl font-medium mt-1 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowAdd(false)}
                className="w-1/2 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-xs font-bold transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={busy}
                className="w-1/2 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl text-xs font-bold shadow transition disabled:opacity-50"
              >
                {busy ? "Submitting..." : "Submit Expense"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}